import type { CaseClaimFragmentFragment } from "@alaffia-technology-solutions/client-sdk";

import { formatDischargeStatus } from "./dischargeStatus";

type ClaimShape = NonNullable<CaseClaimFragmentFragment["claim"]>;
type ClaimFieldKey = Extract<keyof ClaimShape, string>;

export type FieldGroup = "identifiers" | "patient" | "dates" | "codes";

export const GROUP_ORDER: FieldGroup[] = [
  "identifiers",
  "patient",
  "dates",
  "codes",
];

export const GROUP_LABELS: Record<FieldGroup, string> = {
  identifiers: "Identifiers",
  patient: "Patient",
  dates: "Dates",
  codes: "Codes",
};

interface SimpleFieldConfig {
  label: string;
  group: FieldGroup;
  order: number;
  formatter?: (value: unknown) => string;
}

const formatDate = (value: unknown): string => {
  if (typeof value !== "string" || value === "") {
    return "";
  }
  // BatchClaim date fields are ISO strings from Postgraphile; compact yyyy-mm-dd.
  return value.slice(0, 10);
};

export const FIELD_REGISTRY: Partial<Record<ClaimFieldKey, SimpleFieldConfig>> =
  {
    icn: { label: "Claim Number", group: "identifiers", order: 1 },
    clientLineOfBusiness: {
      label: "Line of Business",
      group: "identifiers",
      order: 2,
    },
    patientDob: {
      label: "Patient DOB",
      group: "patient",
      order: 10,
      formatter: formatDate,
    },
    dateOfServiceStart: {
      label: "Service From",
      group: "dates",
      order: 20,
      formatter: formatDate,
    },
    dateOfServiceEnd: {
      label: "Service To",
      group: "dates",
      order: 21,
      formatter: formatDate,
    },
    drgCode: { label: "DRG", group: "codes", order: 30 },
    // ALA-6125: MDRG is the facility-submitted DRG (Horizon `MDRG` column).
    // Sits directly under DRG so reviewers can eyeball paid vs submitted.
    submittedDrg: { label: "MDRG", group: "codes", order: 31 },
    dischargeStatus: {
      label: "Discharge Status",
      group: "codes",
      order: 32,
      formatter: formatDischargeStatus,
    },
    admitDiag: { label: "Admit Diagnosis", group: "codes", order: 33 },
  };

interface DerivedFieldConfig {
  label: string;
  group: FieldGroup;
  order: number;
  compute: (claim: ClaimShape) => string | null;
}

export const DERIVED_REGISTRY: DerivedFieldConfig[] = [
  {
    label: "Patient Name",
    group: "patient",
    order: 9,
    compute: (claim) => {
      const name = claim.patient?.name?.trim();
      return name && name.length > 0 ? name : null;
    },
  },
];

interface PairedAggregateConfig {
  label: string;
  group: FieldGroup;
  order: number;
  aggregate: "paired";
  // Parallel arrays: diagnosis codes at index i pair with POA indicator at same index.
  codeKeys: ClaimFieldKey[];
  poaKeys: ClaimFieldKey[];
}

interface ListAggregateConfig {
  label: string;
  group: FieldGroup;
  order: number;
  aggregate: "list";
  keys: ClaimFieldKey[];
}

type AggregateConfig = PairedAggregateConfig | ListAggregateConfig;

const DIAG_KEYS: ClaimFieldKey[] = [
  "principalDiag",
  "diag1",
  "diag2",
  "diag3",
  "diag4",
  "diag5",
  "diag6",
  "diag7",
  "diag8",
  "diag9",
  "diag10",
  "diag11",
  "diag12",
  "diag13",
  "diag14",
  "diag15",
  "diag16",
  "diag17",
  "diag18",
  "diag19",
  "diag20",
  "diag21",
  "diag22",
  "diag23",
  "diag24",
];

const POA_KEYS: ClaimFieldKey[] = [
  "principalPoa",
  "poa1",
  "poa2",
  "poa3",
  "poa4",
  "poa5",
  "poa6",
  "poa7",
  "poa8",
  "poa9",
  "poa10",
  "poa11",
  "poa12",
  "poa13",
  "poa14",
  "poa15",
  "poa16",
  "poa17",
  "poa18",
  "poa19",
  "poa20",
  "poa21",
  "poa22",
  "poa23",
  "poa24",
];

const PROC_KEYS: ClaimFieldKey[] = [
  "principalProcedureCode",
  "procedureCode1",
  "procedureCode2",
  "procedureCode3",
  "procedureCode4",
  "procedureCode5",
  "procedureCode6",
  "procedureCode7",
  "procedureCode8",
  "procedureCode9",
  "procedureCode10",
];

export const AGGREGATE_REGISTRY: AggregateConfig[] = [
  {
    label: "Diagnosis Codes",
    group: "codes",
    order: 40,
    aggregate: "paired",
    codeKeys: DIAG_KEYS,
    poaKeys: POA_KEYS,
  },
  {
    label: "Procedure Codes",
    group: "codes",
    order: 41,
    aggregate: "list",
    keys: PROC_KEYS,
  },
];
